import { z } from 'zod';
import { CURRENCY_STATUSES } from '../currency/status.js';
/**
 * Contracts for the pre-generated jurisdiction table artifact (see
 * `apps/api/scripts/gen-jurisdictions.ts`) behind the jurisdiction table on
 * Coverage.
 *
 * One row per permitting jurisdiction in `permits.coverage_status` (~16k:
 * ~13k Census places plus ~3k county permit authorities, covered and not).
 * Numbers are normalized to plain JSON by the generation script, so this
 * schema sees `number | null`, never BigQuery's `{ value }` wrappers.
 */
const Fraction = z.number().min(0).max(1).nullable();
const NonNegInt = z.number().int().nonnegative().nullable();
const NonNegNum = z.number().nonnegative().nullable();
/** ISO date ('YYYY-MM-DD') or timestamp string, nullable. */
const NullableDate = z.string().nullable();
const NullableString = z.string().nullable();
/**
 * Per-value permit counts (value → number of permits carrying it). Keyed by the
 * raw `permit_systems` / `tags` value. `.default(() => ({}))` so a pre-existing
 * snapshot generated before these fields existed still parses (as an empty map)
 * — the committed artifact is validated on read, see jurisdictions/store.ts.
 */
const CountsByValue = z.record(z.string(), z.number().int().nonnegative()).default(() => ({}));
export const JurisdictionRow = z.object({
    // identity + geography
    /** Stable composite key: `place:<place_geoid>` or `county:<county_fips>`. */
    id: z.string(),
    /** Census place vs county permit authority (unincorporated-area issuer). */
    kind: z.enum(['place', 'county']),
    /** Census place GEOID; null for county jurisdictions. */
    place_geoid: z.string().nullable(),
    jurisdiction_name: z.string(),
    state: NullableString,
    county_fips: NullableString,
    county_name: NullableString,
    cbsa_name: NullableString,
    population: NonNegInt,
    // our classification of the jurisdiction
    status: z.string(),
    source_id: NullableString,
    /** Scraper/software platform we use (accela, socrata, tyler_energov, …). */
    platform: NullableString,
    /** Source data-quality letter grade (A/B/C/F) from source-quality table. */
    grade: NullableString,
    /** True when we actually hold permits for this place (clickable for detail). */
    covered: z.boolean(),
    // SOCDS / BPS + our counts
    /** Census BPS / HUD SOCDS annual new-permit volume for the jurisdiction. */
    bps_volume: NonNegInt,
    /** Total permits we hold for the jurisdiction. */
    permit_count: NonNegInt,
    // per-permit trade + tag breakdown of the permits we hold (see gen-jurisdictions)
    /**
     * Trade → count: permits carrying each `permit_systems` value (electrical,
     * plumbing, mechanical, roofing, gas, …). A permit can carry several systems,
     * so these sum to more than `permit_count`. Empty `{}` when we hold none.
     */
    trade_counts: CountsByValue,
    /**
     * Tag → count: permits carrying each open-list `tags` keyword. Same
     * multi-count caveat as `trade_counts`; empty `{}` when we hold none.
     */
    tag_counts: CountsByValue,
    /**
     * Tag → permits carrying it among permits dated in the last 10 years, and
     * that 10-year permit total: the prospect CSV's per-tag share (#171).
     * Empty / absent on snapshots written before these shipped.
     */
    tag_counts_10y: CountsByValue,
    permit_count_10y: NonNegInt.default(null),
    // our coverage metrics
    coverage_start: NullableDate,
    coverage_end: NullableDate,
    /** Permits issued in the trailing 12 months of our coverage window. */
    permits_last_12mo: NonNegInt,
    /** permits_last_12mo ÷ population × 10,000 (our estimate). */
    permits_per_10k: NonNegNum,
    /**
     * Feed currency (molluskio#4153, #150): 'current' | 'thin_tail' | 'stale' |
     * 'dateless'. Derived from the trailing-12-month volume + newest permit date so
     * a thin-tail/stale feed isn't presented as current. `.optional()` so snapshots
     * written before this field shipped still parse.
     */
    currency_status: z.enum(CURRENCY_STATUSES).optional(),
    // fill-rate quality (0..1)
    pct_dated: Fraction,
    pct_geocodeable: Fraction,
    pct_resolvable: Fraction,
    pct_error: Fraction,
    // coverage status & blockers
    looks_complete: z.boolean().nullable(),
    blocker_reason: NullableString,
    actionable_next_step: NullableString,
    notes: NullableString,
    // freshness
    last_fetched_at: NullableDate,
    last_mapped_at: NullableDate,
    last_evaluated_at: NullableDate,
});
/** The committed artifact + the body of GET /api/jurisdictions. */
export const JurisdictionsResponse = z.object({
    generated_at: z.string(),
    jurisdictions: z.array(JurisdictionRow),
});
//# sourceMappingURL=jurisdiction.js.map